import '../../instrument.js';
import * as Sentry from '@sentry/react';
import React, { useEffect, useState } from "react";
import './i18n.ts';
import { I18nextProvider, useTranslation } from "react-i18next";

import { Content, WhiteContainer, GlobalStyles, Actions, LoadingSpinner } from '../../../styles/orderform.tsx';
import { checkCookie, generateSegmentName, getLeads, getSekasahkoAddon, getServices, getVariation, isJWT } from "../../../misc/helpers.ts";

import { OrderContext } from "./OrderContext.tsx";

import { apartmentSizes, apartmentTypes, customerTypes, preferences, b2bPhases, b2cPhases, emptyOrder } from "./data.ts";

import { Addon, ApartmentSize, DiscountCodeResponse, DiscountRow, Lead, LeadFormPayload, Order, OrderContextProps, PageViewEventType, Phase, Product } from "./types.ts";
import B2C from "./components/b2c/index.tsx";
import B2B from "./components/b2b/index.tsx";
import { additionalServicesEvent, purchaseEvent, sendPageView } from "../../../misc/analytics.ts";
import PageHeader from "./components/Common/PageHeader.tsx";
import i18next from './i18n.ts';
import SelectedProduct from './components/Common/SelectedProduct.tsx';
import CampaignCode from './components/Common/CampaignCode.tsx';
import CustomerType from './components/Common/CustomerType.tsx';
import { getHubID } from '@hubspot/cms-components';

function OrderForm({ products, addOns, leads, discountRows }: { products: Product[], addOns: Addon[], leads: Lead[], discountRows: DiscountRow[] }) {
  const { t } = useTranslation();

  const [phases, setPhases] = useState<Phase[]>(b2cPhases);
  const [finalPhase, setFinalPhase] = useState<Phase>(b2cPhases.find(phase => phase.showConfirmButton) as Phase);
  const [loading, setLoading] = useState(false);
  const [filteredProducts, setFilteredProducts] = useState<Product[]>(products);
  const [additionalServices, setAdditionalServices] = useState<Addon[]>([]);
  const [currentPhase, setCurrentPhase] = useState<Phase | null>(null);
  const [errorMessage, setErrorMessage] = useState('');

  const [formValues, setFormValues] = useState<Order>(emptyOrder);
  const [includeVAT, setIncludeVAT] = useState(true);
  const [discountInfo, setDiscountInfo] = useState<DiscountCodeResponse | undefined>(undefined);

  useEffect(() => {
    // Initial page load, handle url parameters
    if (typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search);

      const language = urlParams.get('lang') ?? 'fi'
      if (i18next.language !== language) {
        i18next.changeLanguage(language);
      }

      // CHECKOUT PART 2
      // 4. get jwt from urlParams
      const jwt = urlParams.get('jwt');
      // 4.1 Make sure it's valid
      // 4.1.1 Valid: set jwt to sessionStorage and remove from url
      // 4.1.2 invalid: show error message and remove jwt from url
      // 4.1.3 testing: set jwt to sessionStorage and remove from url
      // 5. get order from sessionStorage
      // 6. send order to backend

      if (jwt != null) {
        if (isJWT(jwt)) {
          sessionStorage.setItem('encryptedIdentityToken', jwt as string);
          removeAndCleanUrlParameter('jwt');
          toPhase(6);
          const order = JSON.parse(sessionStorage.getItem('order') as string) as Order;
          if (order.language !== i18next.language) {
            i18next.changeLanguage(order.language);
          }
          if (order) {
            postOrder(order, jwt);
          }
          return
        } else if (jwt == '0') { // Valid JWT for testing
          sessionStorage.setItem('encryptedIdentityToken', jwt);
          removeAndCleanUrlParameter('jwt');
          toPhase(6);
          const order = JSON.parse(sessionStorage.getItem('order') as string);
          if (order) {
            postOrder(order, jwt);
          }
          return
        } else {
          // invalid
          removeAndCleanUrlParameter('jwt');
        }
      }

      if (urlParams.get('error') && urlParams.get('error') == 'access_denied') {
        toPhase(7);
        const errroMessage = 'Vahva tunnistautuminen ei tällä kertaa onnistunut. Yritä myöhemmin uudelleen.';
        setErrorMessage(errroMessage);
        return
      }

      const customerType = urlParams.get('type') ?? ''
      const apartmentType = urlParams.get('apartment-type') ?? ''
      const apartmentSize = urlParams.get('apartment-size') ?? ''
      const consumptionEstimate = urlParams.get('consumption-estimate') ?? ''
      const preference = urlParams.get('preferences') ?? ''
      const channelCode = urlParams.get('channel-code') ?? ''
      const contractId = urlParams.get('contract') ?? ''

      const type = apartmentTypes.map(at => at.value).includes(apartmentType) ? apartmentType : ''
      const variantContractTemplateIds = filteredProducts.map(product => product.variants.map(variant => variant.contract_template_id)).flat();

      setFormValues((prev) => ({
        ...prev,
        'customer-type': customerTypes.map(ct => ct.value).includes(customerType) ? customerType : 'kuluttajat',
        'apartment-type': type,
        'apartment-size': apartmentSizes[type] != null ? (apartmentSizes[type].map((size: ApartmentSize) => size.value).includes(apartmentSize) ? apartmentSize : '') : '',
        'preferences': preferences.map(p => p.value.toLowerCase()).includes(preference) ? String(preference).charAt(0).toUpperCase() + String(preference).slice(1) : '',
        'product': variantContractTemplateIds.includes(contractId) ? contractId : '',
        'channel-code': channelCode ?? '',
      }));

      setTimeout(() => {
        setFormValues((prev) => ({
          ...prev,
          'consumption-estimate': consumptionEstimate ?? '',
        }))
      }, 100)
      toPhase(1);
    }
  }, []);

  useEffect(() => {
    // Scroll to top when phase changes
    if (typeof window !== "undefined") {
      window.scrollTo(0, 0);
    }
    if (!currentPhase) return

    const data: PageViewEventType = {
      apartmentType: formValues['apartment-type'],
      preferences: formValues['preferences'],
      consumptionEstimate: formValues['consumption-estimate'],
      apartmentSize: formValues['apartment-size'],
      product: formValues['product'],
      additionalServices: formValues['additional-services']
    }
    const url = `${window.location.pathname}?step=${currentPhase.key}`;
    const title = currentPhase.label;
    sendPageView(url, title, data);
  }, [currentPhase]);

  useEffect(() => {
    // Filter products based on customer type
    const filtered = products.reduce((acc: Product[], product) => {
      if (
        product.variants?.length > 0 &&
        product.variants.some(variant => includeVAT ? variant.vat.label !== "0" : variant.vat.label === "0") &&
        (includeVAT && !product.tags.some(tag => tag.name === 'Yrityksille') || (!includeVAT && product.tags.some(tag => tag.name === 'Yrityksille'))) &&
        (product.min_usage ? parseInt(formValues['consumption-estimate']) >= product.min_usage : true) &&
        (product.max_usage ? parseInt(formValues['consumption-estimate']) <= product.max_usage : true)
      ) {
        acc.push(product);
      }
      return acc;
    }, []);
    setFilteredProducts(filtered);
  }, [includeVAT, formValues['consumption-estimate'], products]);

  useEffect(() => {
    if (formValues['customer-type'] === '') {
      return;
    }
    const includeVAT = formValues['customer-type'] === 'kuluttajat';
    setIncludeVAT(includeVAT)
    const newPhases = includeVAT ? b2cPhases : b2bPhases;
    setPhases(newPhases);
    setFinalPhase(newPhases.find(phase => phase.showConfirmButton) as Phase);
    setCurrentPhase(newPhases[0]);
  }, [formValues['customer-type']]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value, name, checked, type } = e.target;

    let val: string | undefined = value;

    if (name === 'electric-sauna' || name === 'electric-heating' || name.includes('accept') || name.includes('separated') || name === 'separated-invoicing-address') {
      val = checked ? 'on' : undefined;
    }

    // clear apartment size if apartment type changes
    let apartmentSize = name === 'apartment-size'
      ? value
      : (name === 'apartment-type'
        ? ''
        : formValues['apartment-size']);

    if (name === 'customer-type') {
      // Reset values if customer type changes
      setFormValues({ ...emptyOrder, 'customer-type': value })
    } else if (name === 'product') {
      // reset additional services if product changes
      setFormValues({ ...formValues, [name]: val ?? '', 'additional-services': '' });
    } else {
      setFormValues({ ...formValues, [name]: val, 'apartment-size': apartmentSize });
    }

    if (name === 'additional-services' && value === 'seka') {
      return // do not scroll to next element when setting 'seka' as additional service. It is used for A/B testing purposes.
    }

    if (name === 'campaign-code') {
      setDiscountInfo(undefined);
    }

    scrollTo(name)
  }

  const toAddonsPhase = (contractTemplateId: string) => {
    const services = getServices({
      addOns,
      apartmentType: formValues['apartment-type'],
      electricHeating: formValues['electric-heating'],
      variation: getVariation({ contractTemplateId, products })
    });

    if (services.length > 0 || !includeVAT) {
      if (services.some((service: Addon) => service.group?.label == 'Sähkön alkuperä')) {
        services.unshift(getSekasahkoAddon({ consumption: formValues['consumption-estimate'], includeVAT }));
      }
      setAdditionalServices(services);
      toPhase(3);
    } else {
      toPhase(4);
    }
  }

  const scrollTo = (name: string) => {
    let index = 0;
    let nextElement = '';
    if (currentPhase && currentPhase.key === 1) {
      const elements = formValues['customer-type'] === 'kuluttajat'
        ? ['customer-type', 'apartment-type', 'apartment-size', 'consumption', 'preferences', 'next']
        : ['customer-type', 'business-type', 'preferences', 'consumption', 'next'];
      index = elements.indexOf(name);
      nextElement = elements[index + 1];
    }
    if (currentPhase && currentPhase.key === 3) {
      const checkboxes = document.getElementById('additional-services-checkbox');
      nextElement = 'next';
      if (checkboxes && name === 'additional-service-radio') {
        nextElement = 'additional-services-checkbox';
      }
    }

    if (name == 'accept-terms' && currentPhase && currentPhase.showConfirmButton) {
      nextElement = 'confirm-order';
    }

    // wait for the state to update
    setTimeout(() => {
      const element = document.getElementById(nextElement);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  }

  const toPhase = (key: number, prev = false, productId = '') => {
    const phase = phases.find(phase => phase.key === key);
    if (phase === undefined) {
      return
    }

    if (phase.key === 2 && formValues['product'] != '' && !prev) {
      toAddonsPhase(formValues['product']);
      return;
    }
    if (phase.key === 3 && productId != '') {
      toAddonsPhase(productId);
      return;
    }

    setCurrentPhase(phase);
  }

  const confirmOrder = () => {
    Sentry.setUser({
      email: formValues['email'] || formValues['contact-email']
    });
    Sentry.setContext('formData', {
      formValues: formValues,
    });
    Sentry.captureMessage('Order confirmed, moving to auth', 'info');
    setLoading(true);
    // CHECKOUT PART 1
    // 1. Make sure order is valid // TODO is existing validation enough?
    // 2. save order to sessionStorage
    sessionStorage.setItem('order', JSON.stringify(formValues));
    // 2.1 beginCheckoutEvent to analytics // TODO
    // 3. redirect to auth `https://${window.location.hostname}/_hcms/api/auth-new`
    window.location.replace(`https://${window.location.hostname}/_hcms/api/auth-new?language=${i18next.language}`);
  }

  const sendLeadForm = async (leads: Lead[], order: Order) => {
    const payload: LeadFormPayload = {
      firstName: order["contact-firstname"],
      lastName: order['contact-lastname'],
      email: order['contact-email'],
      companyName: order["company-name"],
      companyBusinessId: order["organization-no"],
      mobileNumber: order['contact-phone'],
      leadSource: 'ostoputki',
      additionalInformation: {
        opportunityProductCategories: leads.map(lead => lead.category_name),
      },
      location: { postalcode: order['location-postalcode'] }
    }

    const apiUrl = '/_hcms/api/integrations/salesforce/lead';
    return fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    });
  }

  const postOrder = async (formData: Order, jwt: string) => {
    setLoading(true);

    // Get leads from additional services
    const selectedLeads = getLeads({ leads, selectedServices: formData['additional-services'] });
    // Remove leads from additional services
    const leadIds = selectedLeads.map(lead => lead.hs_id);
    formData['additional-services'] = formData['additional-services'].split(',').filter((service: string) => !leadIds.includes(Number(service))).join(',');

    const debug = getHubID() !== 4047198; // 4047198 is the production Hub ID
    const conversionFormApi = debug ? 'https://api.hsforms.com/submissions/v3/integration/submit/145563513/3d3b3c01-620e-4738-b6c7-9c1c4a2a4d0a' : 'https://api.hsforms.com/submissions/v3/integration/submit/4047198/c4e4a187-e2ea-422a-b4ec-c85546240e99';
    fetch(conversionFormApi, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        fields: [
          {
            objectTypeId: '0-1',
            name: 'email',
            value: formData.email || formData['contact-email']
          },
          {
            objectTypeId: '0-1',
            name: 'order_form_data',
            value: JSON.stringify(formData)
          }
        ],
        context: {
          hutk: checkCookie('hubspotutk') || null, // include this parameter and set it to the hubspotutk cookie value to enable cookie tracking on your submission
          pageUri: window.location.href,
          pageName: document.title
        },
        legalConsentOptions: {
          legitimateInterest: {
            value: true,
            subscriptionTypeId: 6846831,
            legalBasis: 'CUSTOMER',
            text: 'Olen lukenut sopimusehdot ja tietosuojaselosteen ja hyväksyn ne.'
          }
        }
      })
    }).catch(error => {
      Sentry.captureException(error);
    });

    Sentry.setUser({
      email: formData['email'] || formData['contact-email']
    });
    Sentry.setContext('formData', {
      formValues: formData,
    });
    Sentry.captureMessage('Sending order to backend', 'info');

    fetch(`https://${window.location.hostname}/_hcms/api/order/create`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      },
      signal: AbortSignal.timeout(20 * 1000), // 20 seconds
      body: JSON.stringify({ formData: formData, jwt: jwt })
    })
      .then(async response => {
        if (response.status != 200) {
          throw new Error('Server returned ' + response.status);
        }

        const selectedVariant = getVariation({ products, contractTemplateId: formData['product'] });
        if (selectedVariant) {
          purchaseEvent(formData, selectedVariant);
        }
        additionalServicesEvent(formData['additional-services']);

        // Send lead form to backend if there are any leads
        if (selectedLeads.length > 0) {
          sendLeadForm(selectedLeads, formData)
            .then(response => {
              // console.log('lead form response', response);
            })
            .catch(error => {
              Sentry.captureException(error);
              console.error('lead form error', error);
            });
        }

        // Clear form data to prevent double orders
        sessionStorage.removeItem('order');
        sessionStorage.removeItem('encryptedIdentityToken');

        // Go to thank you page
        const segmentName = generateSegmentName(formData, filteredProducts);
        

        let thankYouUrl = `https://www.lumme-energia.fi/tilaa-sopimus-kiitos?s=${segmentName}`;
        if (i18next.language === 'en') {
          thankYouUrl = 'https://www.lumme-energia.fi/en/thank-you';
        }
        if (formData['customer-type'] === 'yritykset') {
          if (i18next.language === 'en') {
            thankYouUrl = 'https://www.lumme-energia.fi/en/companies/thank-you';
          } else {
            thankYouUrl = 'https://www.lumme-energia.fi/yrityksille/tilaa-sopimus-kiitos';
          }
        }
        if (formData['product'] === '205' || formData['product'] === '314') {
          thankYouUrl = 'https://www.lumme-energia.fi/tilaa-sopimus-kiitos-hjk';
        }
        window.location.replace(thankYouUrl);

      })
      .catch(error => {
        Sentry.captureException(error);
        console.error('order create error', error);
        toPhase(7);
        const errroMessage = t('errors.orderCreation');
        setErrorMessage(errroMessage);
        setLoading(false);
      });
  }

  const removeAndCleanUrlParameter = (name: string) => {
    const url = new URL(window.location.href);
    url.searchParams.delete(name);
    window.history.replaceState({}, '', url);
  }

  useEffect(() => {
    setFormValues((prev) => ({
      ...prev,
      language: i18next.language
    }));
  }, [i18next.language]);

  const context = {
    formValues,
    setFormValues,
    handleFormChange: handleChange,
    products: filteredProducts,
    addOns: additionalServices,
    includeVAT,
    toPhase,
    confirmOrder,
    leads,
    discountInfo,
    setDiscountInfo,
    discountRows,
  } as OrderContextProps;

  return (
    <main>
      <I18nextProvider i18n={i18next}>
        <GlobalStyles />
        <OrderContext.Provider value={context}>
          {currentPhase &&
            <>
              <PageHeader currentPhase={currentPhase} phases={phases} />
              <form autoComplete="off">
                <Content>

                  {currentPhase.key < finalPhase.key && <SelectedProduct showChangeButton={currentPhase.key > 1} />}

                  {currentPhase.key === 1 && (
                    <>
                      {/* <CampaignCode /> */}
                      <CustomerType />
                    </>
                  )}

                  {formValues['customer-type'] === 'kuluttajat' && (
                    <B2C currentPhase={currentPhase} />
                  )}

                  {formValues['customer-type'] === 'yritykset' && (
                    <B2B currentPhase={currentPhase} />
                  )}


                  {currentPhase.value === 'loading' && (
                    <WhiteContainer>
                      <h1>{t('common.processingOrder')}</h1>
                      <p>{t('common.processingTime')}</p>
                    </WhiteContainer>
                  )}

                  {currentPhase.value === 'error' && (
                    <WhiteContainer>
                      <h1>{t('errors.woops')}</h1>
                      <p>{errorMessage}</p>
                    </WhiteContainer>
                  )}

                  <Actions id="next">
                    {loading && (
                      <LoadingSpinner></LoadingSpinner>
                    )}
                  </Actions>

                </Content>
              </form>
            </>}
        </OrderContext.Provider>
      </I18nextProvider>
    </main>
  );
}
export default OrderForm;
